import React, { useState, useEffect, useRef } from "react";
import { User, LogOut, Trash2, CreditCard, Zap, Bell, BellOff, HelpCircle, PlayCircle, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API_ROOT } from "@/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import useSubscription from "@/hooks/useSubscription";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface SidebarProfileProps {
  className?: string;
  style?: React.CSSProperties;
  collapsed?: boolean;
  onStartTour?: () => void;
}

interface CurrentUser {
  email: string;
  first_name: string;
  surname: string;
  company_name?: string;
  postal_code?: string;
  country?: string;
  street_name?: string;
  street_number?: string;
  city?: string;
  email_notifications_enabled?: boolean;
}

const SidebarProfile: React.FC<SidebarProfileProps> = ({ className = "", style = {}, collapsed = false, onStartTour }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [editableUser, setEditableUser] = useState<CurrentUser | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { subscription, createBillingPortalSession } = useSubscription();

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${API_ROOT}/users/me`, {
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          }
        });
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
          setEditableUser(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials - updated to match CollapsedProfile logic
  const getUserInitials = (first_name: string, surname: string, email: string) => {
    if (first_name && surname) {
      return (first_name[0] + surname[0]).toUpperCase();
    } else if (first_name) {
      return first_name.substring(0, 2).toUpperCase();
    } else {
      // Fallback to email if no name
      return email.substring(0, 2).toUpperCase();
    }
  };

  // Get formatted user name (First name + first letter of surname)
  const getFormattedUserName = (first_name: string, surname: string, email: string) => {
    if (first_name && surname) {
      return `${first_name} ${surname[0]}.`;
    } else if (first_name) {
      return first_name;
    } else {
      return email.split('@')[0];
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("userType");
    localStorage.removeItem("brandcomply_intro_shown");
    localStorage.removeItem("brandcomply_tour_completed");
    sessionStorage.removeItem("trial_banner_dismissed");
    navigate("/auth");
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_ROOT}/users/me`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (response.ok) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("userType");
        localStorage.removeItem("brandcomply_intro_shown");
        localStorage.removeItem("brandcomply_tour_completed");
        sessionStorage.removeItem("trial_banner_dismissed");
        navigate("/auth");
      } else {
        throw new Error("Failed to delete account");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSaveAccount = async () => {
    if (!editableUser) return;
    try {
      const response = await fetch(`${API_ROOT}/users/me`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          company_name: editableUser.company_name || "",
          postal_code: editableUser.postal_code || "",
          country: editableUser.country || "",
          street_name: editableUser.street_name || "",
          street_number: editableUser.street_number || "",
          city: editableUser.city || "",
          first_name: editableUser.first_name || "",
          surname: editableUser.surname || ""
        })
      });
      if (!response.ok) {
        throw new Error("Failed to update account");
      }
      const updated = await response.json();
      setCurrentUser(updated);
      setEditableUser(updated);
      setIsEditing(false);
      setShowAccountDetails(false);
    } catch (error) {
      console.error("Error updating account:", error);
      alert("Failed to update account. Please try again.");
    }
  };

  // Toggle email notifications
  const handleToggleNotifications = async () => {
    if (!currentUser) return;
    
    setIsTogglingNotifications(true);
    const newValue = !currentUser.email_notifications_enabled;
    
    try {
      const response = await fetch(`${API_ROOT}/users/me/notifications`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ enabled: newValue })
      });
      
      if (!response.ok) {
        throw new Error("Failed to update notification preference");
      }
      
      // Update local state
      setCurrentUser(prev => prev ? { ...prev, email_notifications_enabled: newValue } : null);
      console.log(`✅ Email notifications ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Error updating notifications:", error);
      alert("Failed to update notification preference. Please try again.");
    } finally {
      setIsTogglingNotifications(false);
    }
  };

  if (!currentUser) {
    return null; // Don't render anything if user is not loaded
  }

  return (
    <div className={`relative ${className}`} ref={userDropdownRef} style={style}>
      <button
        onClick={() => setUserDropdownOpen(!userDropdownOpen)}
        className={`rounded-lg cursor-pointer transition-all border-none bg-transparent text-left ${collapsed ? '' : 'hover:bg-gray-100'}`}
        title={currentUser ? `${currentUser.first_name} ${currentUser.surname}` || currentUser.email : "User Profile"}
        style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? '0' : '12px',
          outline: 'none',
          fontFamily: 'inherit',
          width: 'calc(100% - 20px)',
          margin: '8px 10px',
          padding: '10px',
          justifyContent: 'flex-start',
          minHeight: '44px'
        }}
      >
        {/* Avatar Circle */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ 
            background: PALETTE.paleViolet,
            color: PALETTE.brightSnow
          }}
        >
          <span className="text-xs font-semibold">
            {getUserInitials(currentUser.first_name, currentUser.surname, currentUser.email)}
          </span>
        </div>
        
        {/* User Name */}
        {!collapsed && (
          <div style={{ flex: '1', minWidth: '0' }}>
            <div 
              className="text-sm font-medium truncate" 
              style={{ color: PALETTE.gunmetal }}
            >
              {getFormattedUserName(currentUser.first_name, currentUser.surname, currentUser.email)}
            </div>
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {userDropdownOpen && (
        <div 
          className="absolute bottom-full left-full ml-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
          style={{ background: PALETTE.brightSnow }}
        >
          {/* User info entry - clickable to open account settings */}
          <div 
            className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => {
              setShowAccountDetails(true);
              setUserDropdownOpen(false);
            }}
          >
            <div className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
              {currentUser.first_name && currentUser.surname 
                ? `${currentUser.first_name} ${currentUser.surname}`
                : currentUser.first_name || currentUser.email.split('@')[0]
              }
            </div>
            <div className="text-xs" style={{ color: `${PALETTE.gunmetal}70` }}>
              {currentUser.email}
            </div>
          </div>

          {/* Subscription Status */}
          <div 
            className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => {
              navigate('/subscription');
              setUserDropdownOpen(false);
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" style={{ color: PALETTE.inchworm }} />
                <span className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                  {subscription?.status === 'trialing'
                    ? `14-Day Free Trial`
                    : subscription?.has_subscription && subscription?.status === 'active' 
                      ? subscription.plan_name 
                      : 'No Plan'
                  }
                </span>
              </div>
              {subscription?.has_subscription && (subscription?.status === 'active' || subscription?.status === 'trialing') ? (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${PALETTE.inchworm}20`, color: PALETTE.gunmetal }}>
                  {subscription?.status === 'trialing' ? `${(subscription as any)?.trial_days_remaining ?? 0}d left` : 'Active'}
                </span>
              ) : (
                <span className="text-xs font-medium" style={{ color: PALETTE.orange }}>
                  Upgrade
                </span>
              )}
            </div>
            {subscription && (
              <div className="mt-1">
                <div className="flex items-center justify-between text-xs mb-1" style={{ color: `${PALETTE.gunmetal}60` }}>
                  <span>{subscription.current_usage}/{subscription.monthly_limit} checks</span>
                  <span>
                    {subscription.remaining} left
                    {subscription.current_period_end && ` until ${new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  </span>
                </div>
                {subscription?.status === 'trialing' && (
                  <div className="text-xs mt-1" style={{ color: `${PALETTE.gunmetal}50` }}>
                    No credit card required during trial
                  </div>
                )}
                <div className="h-1 rounded-full overflow-hidden" style={{ background: `${PALETTE.americanSilver}30` }}>
                  <div 
                    className="h-full rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.min(100, (subscription.current_usage / subscription.monthly_limit) * 100)}%`,
                      background: subscription.current_usage >= subscription.monthly_limit * 0.9 
                        ? PALETTE.orange 
                        : PALETTE.inchworm
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Manage Subscription - only show if has subscription */}
          {subscription?.has_subscription && subscription?.status === 'active' && (
            <div 
              className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={async () => {
                try {
                  const { portal_url } = await createBillingPortalSession();
                  if (portal_url) {
                    window.location.href = portal_url; // Redirect to Stripe customer portal
                  } else {
                    console.error('No customer portal URL returned');
                    alert('Unable to open subscription portal. Please try again.');
                  }
                } catch (error) {
                  console.error('Failed to open customer portal:', error);
                  alert('Failed to open subscription portal. Please try again.');
                }
                setUserDropdownOpen(false);
              }}
            >
              <div className="flex items-center gap-3">
                <CreditCard className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
                <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                  Manage Subscription
                </span>
              </div>
            </div>
          )}

          {/* Email Notifications Toggle */}
          <div 
            className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={handleToggleNotifications}
            title={
              currentUser.email_notifications_enabled !== false
                ? 'Get notified when videos are submitted'
                : 'Notifications are disabled'
            }
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {currentUser.email_notifications_enabled !== false ? (
                  <Bell className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
                ) : (
                  <BellOff className="w-4 h-4" style={{ color: PALETTE.americanSilver }} />
                )}
                <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                  Email Notifications
                </span>
              </div>
              {/* Toggle Switch */}
              <div 
                className={`relative w-9 h-5 rounded-full transition-colors ${isTogglingNotifications ? 'opacity-50' : ''}`}
                style={{ 
                  background: currentUser.email_notifications_enabled !== false 
                    ? PALETTE.inchworm 
                    : PALETTE.americanSilver 
                }}
              >
                <div 
                  className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                  style={{ 
                    transform: currentUser.email_notifications_enabled !== false 
                      ? 'translateX(18px)' 
                      : 'translateX(2px)' 
                  }}
                />
              </div>
            </div>
          </div>

          {/* Help */}
          <div 
            className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => setShowHelp(true)}
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
              <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                Help & Support
              </span>
            </div>
          </div>

          {/* Delete Account */}
          <div 
            className="px-4 py-2 hover:bg-red-50 cursor-pointer transition-colors"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <div className="flex items-center gap-3">
              <Trash2 className="w-4 h-4" style={{ color: PALETTE.orange }} />
              <span className="text-sm" style={{ color: PALETTE.orange }}>
                Delete Account
              </span>
            </div>
          </div>

          {/* Logout */}
          <div 
            className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={handleLogout}
          >
            <div className="flex items-center gap-3">
              <LogOut className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
              <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                Logout
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      <Dialog
        open={showAccountDetails}
        onOpenChange={(open) => {
          setShowAccountDetails(open);
          if (open) {
            setIsEditing(false);
            setEditableUser(currentUser);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Account Information</DialogTitle>
          </DialogHeader>
          {editableUser && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Company</label>
                <input
                  className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                  value={editableUser.company_name || ''}
                  onChange={(e) => setEditableUser({ ...editableUser, company_name: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Street Name</label>
                  <input
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    value={editableUser.street_name || ''}
                    onChange={(e) => setEditableUser({ ...editableUser, street_name: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Street Number</label>
                  <input
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    value={editableUser.street_number || ''}
                    onChange={(e) => setEditableUser({ ...editableUser, street_number: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Postal Code</label>
                  <input
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    value={editableUser.postal_code || ''}
                    onChange={(e) => setEditableUser({ ...editableUser, postal_code: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>City</label>
                  <input
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    value={editableUser.city || ''}
                    onChange={(e) => setEditableUser({ ...editableUser, city: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Country</label>
                <input
                  className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                  value={editableUser.country || ''}
                  onChange={(e) => setEditableUser({ ...editableUser, country: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>First Name</label>
                  <input
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    value={editableUser.first_name || ''}
                    onChange={(e) => setEditableUser({ ...editableUser, first_name: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Surname</label>
                  <input
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${isEditing ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    value={editableUser.surname || ''}
                    onChange={(e) => setEditableUser({ ...editableUser, surname: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: `${PALETTE.gunmetal}70` }}>Email</label>
                <input
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm bg-gray-50"
                  value={editableUser.email || ''}
                  disabled
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {!isEditing ? (
                  <>
                    <Button
                      onClick={() => setIsEditing(true)}
                      style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveAccount}
                      style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                    >
                      Save Changes
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Help &amp; Support</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Self-Onboarding Tour Section */}
            {onStartTour && (
              <div 
                className="p-4 rounded-lg border"
                style={{ 
                  background: `${PALETTE.inchworm}10`,
                  borderColor: `${PALETTE.inchworm}40`
                }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: PALETTE.inchworm }}
                  >
                    <PlayCircle className="w-5 h-5" style={{ color: PALETTE.gunmetal }} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm mb-1" style={{ color: PALETTE.gunmetal }}>
                      Interactive Product Tour
                    </h4>
                    <p className="text-xs text-gray-600 mb-3">
                      Take a guided tour through BrandComply's features. Learn how to create campaigns, 
                      manage submissions, and get the most out of the platform.
                    </p>
                    <Button
                      onClick={() => {
                        setShowHelp(false);
                        setUserDropdownOpen(false);
                        // Small delay to let the modal close
                        setTimeout(() => onStartTour(), 150);
                      }}
                      size="sm"
                      className="flex items-center gap-2"
                      style={{ 
                        background: PALETTE.gunmetal, 
                        color: PALETTE.brightSnow,
                        borderRadius: '8px'
                      }}
                    >
                      <PlayCircle className="w-4 h-4" />
                      Start Tour
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Support Section */}
            <div className="p-4 rounded-lg bg-gray-50">
              <div className="flex items-start gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${PALETTE.paleViolet}30` }}
                >
                  <Mail className="w-5 h-5" style={{ color: PALETTE.paleViolet }} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm mb-1" style={{ color: PALETTE.gunmetal }}>
                    Contact Support
                  </h4>
                  <p className="text-xs text-gray-600 mb-2">
                    Need help or have questions? Our team is here to assist you.
                  </p>
                  <a 
                    href="mailto:info@brandcomply.ai" 
                    className="text-sm font-medium underline hover:no-underline"
                    style={{ color: PALETTE.gunmetal }}
                  >
                    info@brandcomply.ai
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Tips */}
            <div className="text-xs text-gray-500">
              <p className="font-medium mb-2" style={{ color: PALETTE.gunmetal }}>Quick Tips:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Click the <HelpCircle className="w-3 h-3 inline" /> button in the top right to start a tour anytime</li>
                <li>Use the Feature Request button to suggest new features</li>
                <li>Check your subscription status in the sidebar</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This action cannot be undone. This will permanently delete your account and remove your data from our servers.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete Account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SidebarProfile; 